import { useEffect, useRef } from 'react';
import {
  deleteChat,
  formatRelativeChatTime,
  type ChatSession,
} from '../lib/splashChatStore';

/**
 * Past-chats popover anchored under the clock icon in Splash's banner.
 *
 * Click outside / Esc closes. Click a row swaps the active chat in the
 * pane. A small trash icon on hover deletes (with confirm). The "+"
 * button for "new chat" lives in the banner itself, not in this
 * popover.
 *
 * Sized for ~320px wide; positioned absolutely by the parent.
 */
export function ChatHistoryPopover({
  chats,
  activeChatId,
  onPick,
  onClose,
  onChatsChanged,
  userEmail,
}: {
  chats: ChatSession[];
  activeChatId: string | null;
  onPick: (id: string) => void;
  onClose: () => void;
  onChatsChanged: () => void;
  userEmail: string | null | undefined;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function onOutside(e: MouseEvent) {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target as Node)) return;
      onClose();
    }
    window.addEventListener('keydown', onKey);
    // Defer click-outside listener to next tick so the click that
    // OPENED the popover doesn't immediately close it.
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onOutside);
    }, 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onOutside);
      clearTimeout(t);
    };
  }, [onClose]);

  function onDelete(id: string, ev: React.MouseEvent) {
    ev.stopPropagation();
    // Lightweight confirm — we're not in a critical-data context.
    if (!window.confirm('Delete this chat? This can\'t be undone.')) return;
    deleteChat(id, userEmail);
    onChatsChanged();
  }

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Past chats"
      style={{
        position: 'absolute',
        top: '100%',
        right: 8,
        marginTop: 6,
        width: 320,
        maxHeight: '70vh',
        background: 'var(--aqua-surface)',
        color: 'var(--aqua-text)',
        border: '1px solid var(--aqua-border)',
        borderRadius: 10,
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        zIndex: 50,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid var(--aqua-border)',
          fontWeight: 600,
          fontSize: '0.85rem',
          color: 'var(--aqua-text-strong)',
        }}
      >
        Past chats
      </div>

      {chats.length === 0 ? (
        <div
          style={{
            padding: '16px 14px',
            fontSize: '0.85rem',
            color: 'var(--aqua-text-muted, #6c757d)',
            fontStyle: 'italic',
          }}
        >
          No past chats yet. Your conversations will show up here once
          you've sent a few messages.
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {chats.map((c) => {
            const isActive = c.id === activeChatId;
            const turnCount = c.messages.length;
            return (
              <li
                key={c.id}
                onClick={() => onPick(c.id)}
                style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid var(--aqua-border)',
                  cursor: 'pointer',
                  background: isActive
                    ? 'var(--aqua-surface-muted, rgba(1,157,220,0.08))'
                    : 'transparent',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLLIElement).style.background =
                      'var(--aqua-surface-muted, rgba(0,0,0,0.03))';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLLIElement).style.background =
                    isActive
                      ? 'var(--aqua-surface-muted, rgba(1,157,220,0.08))'
                      : 'transparent';
                }}
              >
                <div
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: 'var(--aqua-text-strong)',
                    paddingRight: 24,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {c.title}
                </div>
                <div
                  style={{
                    marginTop: 3,
                    fontSize: '0.72rem',
                    color: 'var(--aqua-text-muted, #6c757d)',
                    display: 'flex',
                    gap: 8,
                  }}
                >
                  <span>{formatRelativeChatTime(c.lastTurnAt)}</span>
                  <span>·</span>
                  <span>
                    {turnCount} message{turnCount === 1 ? '' : 's'}
                  </span>
                  {isActive ? (
                    <>
                      <span>·</span>
                      <span style={{ color: 'var(--aqua-primary)', fontWeight: 600 }}>
                        Active
                      </span>
                    </>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={(ev) => onDelete(c.id, ev)}
                  title="Delete this chat"
                  aria-label="Delete this chat"
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--aqua-text-muted, #6c757d)',
                    fontSize: 14,
                    cursor: 'pointer',
                    padding: 4,
                    lineHeight: 1,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color = '#dc3545';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.color =
                      'var(--aqua-text-muted, #6c757d)';
                  }}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
